import { sdk } from '../sdk';
import { getErrorMessage } from '../utils/errorMessage';

export interface DonationType {
  _id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DonationTypeWithAmounts extends DonationType {
  periodAmount: number;
  periodCount: number;
}

export interface CreateDonationTypeData {
  name: string;
  description?: string;
}

export interface UpdateDonationTypeData {
  name?: string;
  description?: string;
  isActive?: boolean;
}

export type TimePeriod = 'month' | 'biAnnual' | 'annual';

// Custom response type for the amounts endpoint
export interface DonationTypesWithAmountsResponse {
  success: boolean;
  message?: string;
  data: DonationTypeWithAmounts[];
  period: {
    type: string;
    startDate: string;
    endDate: string;
  };
}

export class DonationTypesAPI {
  // Get all donation types
  static async getAllDonationTypes(): Promise<DonationType[]> {
    try {
      const response = await sdk.client.get('/api/v1/donation-types');
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch donation types');
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to fetch donation types'));
    }
  }

  // Get donation types with amounts for specific time period
  static async getDonationTypesWithAmounts(period: TimePeriod = 'month'): Promise<DonationTypesWithAmountsResponse> {
    try {
      const response = await sdk.client.get(`/api/v1/donation-types/amounts?period=${period}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch donation types with amounts');
      }
      
      return response as DonationTypesWithAmountsResponse;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to fetch donation types with amounts'));
    }
  }

  // Get donation type by ID
  static async getDonationTypeById(id: string): Promise<DonationType> {
    try {
      const response = await sdk.client.get(`/api/v1/donation-types/${id}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch donation type');
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to fetch donation type'));
    }
  }

  // Create new donation type
  static async createDonationType(data: CreateDonationTypeData): Promise<DonationType> {
    try {
      const response = await sdk.client.post('/api/v1/donation-types', data);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create donation type');
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to create donation type'));
    }
  }

  // Update donation type
  static async updateDonationType(id: string, data: UpdateDonationTypeData): Promise<DonationType> {
    try {
      const response = await sdk.client.put(`/api/v1/donation-types/${id}`, data);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update donation type');
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to update donation type'));
    }
  }

  // Delete donation type (soft delete)
  static async deleteDonationType(id: string): Promise<void> {
    try {
      const response = await sdk.client.delete(`/api/v1/donation-types/${id}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete donation type');
      }
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to delete donation type'));
    }
  }

  // Search donation types
  static async searchDonationTypes(query: string): Promise<DonationType[]> {
    try {
      const response = await sdk.client.get(`/api/v1/donation-types/search?query=${encodeURIComponent(query)}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to search donation types');
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to search donation types'));
    }
  }
}
